import { useMemo } from "react";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import {
    resolveTimelineRange,
    toTimelineDateFilterProps,
} from "./activityTimelineRange.lib";
import type { StoredLastVisit } from "./lastVisit/lastVisit.lib";
import { useLastVisit } from "./lastVisit/useLastVisit";
import { useToday } from "./useToday";

interface UseActivityTimelineParams {
    dateRange: DateOnlyDateRange;
    /** This page's stored last visit, wherever the page keeps it. */
    stored: StoredLastVisit | undefined;
    save: (visit: StoredLastVisit) => void;
    /** When history starts, e.g. the workspace's creation. */
    historyStartAt?: Date;
}

/**
 * An Activity timeline's window and start: the picked date range, or "Since
 * your last visit" without one. The page's list shows the window.
 */
export function useActivityTimeline({
    dateRange: { start, end },
    stored,
    save,
    historyStartAt,
}: UseActivityTimelineParams) {
    const { since, rememberVisit } = useLastVisit({ stored, save });
    const today = useToday();
    const historyStartTime = historyStartAt?.getTime();
    const range = useMemo(
        () =>
            resolveTimelineRange({
                dateRange: { start, end },
                lastVisitSince: since,
                today,
                historyStartAt:
                    historyStartTime === undefined
                        ? undefined
                        : new Date(historyStartTime),
            }),
        [start, end, since, today, historyStartTime],
    );

    return {
        range,
        today,
        rememberVisit,
        dateFilterProps: toTimelineDateFilterProps(range),
    };
}
